import { transformPoint, type Matrix, type Rect } from '../core/geometry';

// Partition destination *pixels*, not antialiased tile quads. Every pixel center
// belongs to exactly one source tile even under rotation, reflection or shear.
// Outer source edges remain the browser's responsibility when drawing the image.
export function clipTileCoverage(
  context: CanvasRenderingContext2D, inverse: Matrix, matrix: Matrix,
  tile: Rect, sourceWidth: number, sourceHeight: number,
  targetWidth: number, targetHeight: number,
): void {
  const corners = [
    transformPoint(matrix, { x: tile.x, y: tile.y }),
    transformPoint(matrix, { x: tile.x + tile.width, y: tile.y }),
    transformPoint(matrix, { x: tile.x, y: tile.y + tile.height }),
    transformPoint(matrix, { x: tile.x + tile.width, y: tile.y + tile.height }),
  ];
  const firstRow = Math.max(0, Math.floor(Math.min(...corners.map(p => p.y))) - 1);
  const lastRow = Math.min(targetHeight, Math.ceil(Math.max(...corners.map(p => p.y))) + 1);
  const minX = tile.x === 0 ? -Infinity : tile.x;
  const maxX = tile.x + tile.width === sourceWidth ? Infinity : tile.x + tile.width;
  const minY = tile.y === 0 ? -Infinity : tile.y;
  const maxY = tile.y + tile.height === sourceHeight ? Infinity : tile.y + tile.height;
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.beginPath();
  let previousLeft = 0; let previousRight = 0; let runStart = firstRow;
  for (let y = firstRow; y <= lastRow; y++) {
    let left = 0; let right = targetWidth;
    for (const [p, q, min, max] of [
      [inverse[0], inverse[0] * 0.5 + inverse[2] * (y + 0.5) + inverse[4], minX, maxX],
      [inverse[1], inverse[1] * 0.5 + inverse[3] * (y + 0.5) + inverse[5], minY, maxY],
    ] as const) {
      if (p > 0) { left = Math.max(left, Math.ceil((min - q) / p)); right = Math.min(right, Math.ceil((max - q) / p)); }
      else if (p < 0) { left = Math.max(left, Math.floor((max - q) / p) + 1); right = Math.min(right, Math.floor((min - q) / p) + 1); }
      else if (q < min || q >= max) { right = left; }
    }
    if (y === lastRow || left !== previousLeft || right !== previousRight) {
      if (previousRight > previousLeft && y > runStart) context.rect(previousLeft, runStart, previousRight - previousLeft, y - runStart);
      previousLeft = left; previousRight = right; runStart = y;
    }
  }
  context.clip();
}
